import React, { useEffect, useState } from 'react';
import { Chip, IconButton, Tooltip } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CenterFocusWeakIcon from '@material-ui/icons/CenterFocusWeak';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ColorizeIcon from '@material-ui/icons/Colorize';
import DescriptionIcon from '@material-ui/icons/Description';
import ErrorIcon from '@material-ui/icons/Error';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import LockIcon from '@material-ui/icons/Lock';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { ATTACHABLES, ATTACHABLES_LABELS } from '../../constants/AttachmentConstants';

// Cell renderers for AttachmentDataTable. Every renderer accepts either the
// row object or, when material-table groups by that column, the bare field
// value — so each one guards with isRow before touching row fields.

const isRow = input => input !== null && typeof input === 'object';

// Light background + dark text on every chip (>= 4.5:1), so the chips stay
// readable on both the light and the soft-dark admin surfaces.
const TONES = {
  indigo: { bg: '#e8eaf6', fg: '#283593' },
  blue: { bg: '#e3f2fd', fg: '#0d47a1' },
  purple: { bg: '#f3e5f5', fg: '#6a1b9a' },
  green: { bg: '#e8f5e9', fg: '#1b5e20' },
  red: { bg: '#ffebee', fg: '#b71c1c' },
  amber: { bg: '#fff8e1', fg: '#5d4037' },
  grey: { bg: '#eceff1', fg: '#37474f' },
};

const TYPE_STYLES = {
  [ATTACHABLES.EHC_SERVICE_ORDER]: { Icon: AssignmentIcon, tone: 'indigo' },
  [ATTACHABLES.AFYA_MEDICAL_RESULT]: { Icon: DescriptionIcon, tone: 'blue' },
  radiology: { Icon: CenterFocusWeakIcon, tone: 'blue' },
  laboratory: { Icon: ColorizeIcon, tone: 'purple' },
};

const WHATSAPP_STYLES = {
  sent: { Icon: CheckCircleIcon, tone: 'green', label: 'Sent' },
  failed: { Icon: ErrorIcon, tone: 'red', label: 'Failed' },
  pending: { Icon: ScheduleIcon, tone: 'grey', label: 'Pending' },
};

const SHARE_STYLES = {
  active: { Icon: LinkIcon, tone: 'green', label: 'Active' },
  expired: { Icon: LinkOffIcon, tone: 'grey', label: 'Expired' },
  locked: { Icon: LockIcon, tone: 'amber', label: 'Locked' },
};

const FALLBACK_STYLE = { Icon: DescriptionIcon, tone: 'grey' };

// size="small" is a 30px button; padding 10 brings the hit area to 44px.
const ICON_BUTTON_STYLE = { padding: 10 };
const TRUNCATE_STYLE = { display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const SECONDARY_STYLE = { fontSize: '0.75rem', color: 'var(--theme-text-muted, #636e72)' };
const MUTED_DASH = <span style={{ color: 'var(--theme-text-subtle, #b2bec3)' }}>—</span>;

// The label gets its own colour node: the soft-dark theme forces
// `.MuiChip-root { color: #fff !important }`, which would beat an inline
// colour on the root. tabIndex makes the chip focusable so the tooltip
// (failure reason, expiry) also opens from the keyboard.
const StatusChip = ({ Icon, tone, label, title }) => {
  const { bg, fg } = TONES[tone];
  const chip = (
    <Chip
      size="small"
      tabIndex={title ? 0 : undefined}
      icon={<Icon style={{ color: fg, fontSize: 16 }} />}
      label={<span style={{ color: fg }}>{label}</span>}
      style={{ backgroundColor: bg, fontWeight: 500 }}
    />
  );
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
};

const Truncate = ({ text, href, style }) => (
  <Tooltip title={text || ''} enterDelay={400}>
    <span style={{ ...TRUNCATE_STYLE, ...style }}>{href ? <a href={href}>{text}</a> : text}</span>
  </Tooltip>
);

const Stacked = ({ primary, href, secondary }) => (
  <div style={{ lineHeight: 1.3 }}>
    <Truncate text={primary || '—'} href={primary ? href : undefined} />
    {[].concat(secondary).filter(Boolean).map((line, index) => (
      <Truncate key={index} text={line} style={SECONDARY_STYLE} />
    ))}
  </div>
);

const LinkButton = ({ href, label, title, children }) => (
  <Tooltip title={title || label}>
    <IconButton size="small" style={ICON_BUTTON_STYLE} component="a" href={href} target="_blank" rel="noopener noreferrer" aria-label={label} color="default">
      {children}
    </IconButton>
  </Tooltip>
);

const CopyButton = ({ text }) => {
  const [result, setResult] = useState(null); // null | 'ok' | 'fail'

  useEffect(() => {
    if (!result) return undefined;
    const timer = setTimeout(() => setResult(null), 1800);
    return () => clearTimeout(timer);
  }, [result]);

  const copy = () => {
    if (!navigator.clipboard) return setResult('fail');
    return navigator.clipboard.writeText(text).then(() => setResult('ok'), () => setResult('fail'));
  };

  const label = { ok: 'Copied', fail: 'Copy failed' }[result] || 'Copy short link';
  const Icon = { ok: CheckCircleIcon, fail: ErrorIcon }[result] || FileCopyIcon;
  return (
    <Tooltip title={label}>
      <IconButton size="small" style={ICON_BUTTON_STYLE} onClick={copy} aria-label={label} color={result === 'ok' ? 'primary' : 'default'}>
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// The admin is served under an optional /:locale prefix; keep it on the link.
const profilePath = id => `${(window.location.pathname.match(/^\/(en|id)(?=\/|$)/) || [''])[0]}/profiles/${id}`;

export const renderType = input => {
  const type = isRow(input) ? input.attachable_type : input;
  const result = isRow(input) ? input.medical_result : null;
  const style = TYPE_STYLES[result ? result.result_type : type] || FALLBACK_STYLE;
  const label = (result && result.result_type_label) || ATTACHABLES_LABELS[type] || type;
  return <StatusChip {...style} label={label} />;
};

export const renderPatient = input => {
  if (!isRow(input)) return input;
  const result = input.medical_result;
  const name = input.profile_full_name || (result && result.patient_name);
  const mrn = result && result.patient_mrn;
  return (
    <div style={{ lineHeight: 1.3 }}>
      <Truncate text={name || '—'} href={name && input.profile_id ? profilePath(input.profile_id) : undefined} />
      {mrn && <span style={{ ...SECONDARY_STYLE, fontVariantNumeric: 'tabular-nums' }}>MRN {mrn}</span>}
    </div>
  );
};

export const renderDocument = input => {
  if (!isRow(input)) return input;
  const result = input.medical_result;
  if (!result) return <Stacked primary={input.attachment_number} secondary={input.assessment_type} />;
  return <Stacked primary={result.title || result.lab_number} secondary={result.title ? result.lab_number : null} />;
};

export const renderContext = input => {
  if (!isRow(input)) return input;
  const result = input.medical_result;
  if (result) {
    return <Stacked primary={result.branch_name} secondary={result.issued_at_label && `Issued ${result.issued_at_label}`} />;
  }
  return <Stacked primary={input.doctor_full_name} href={input.doctor_url} secondary={[input.polyclinic, input.guarantor]} />;
};

export const renderDelivery = input => {
  const result = isRow(input) ? input.medical_result : { whatsapp_status: input };
  if (!result) return MUTED_DASH;
  const style = WHATSAPP_STYLES[result.whatsapp_status] || { ...FALLBACK_STYLE, label: result.whatsapp_status };
  const detail = {
    sent: result.whatsapp_sent_at_label && `WhatsApp sent ${result.whatsapp_sent_at_label}`,
    failed: result.whatsapp_error,
    pending: 'WhatsApp message not sent yet',
  }[result.whatsapp_status];
  const chip = <StatusChip {...style} title={detail || ''} />;
  if (!isRow(input) || result.whatsapp_status !== 'failed' || !result.whatsapp_error) return chip;
  return (
    <div style={{ lineHeight: 1.3 }}>
      {chip}
      <Truncate text={result.whatsapp_error} style={SECONDARY_STYLE} />
    </div>
  );
};

export const renderLink = input => {
  const result = isRow(input) ? input.medical_result : { share_state: input };
  if (!result) return MUTED_DASH;
  const style = SHARE_STYLES[result.share_state] || { ...FALLBACK_STYLE, label: result.share_state };
  const detail = {
    active: result.expires_at_label && `Expires ${result.expires_at_label}`,
    expired: result.expires_at_label && `Expired ${result.expires_at_label}`,
    locked: 'Locked after too many wrong dates of birth',
  }[result.share_state];
  const chip = <StatusChip {...style} title={detail || ''} />;
  if (!isRow(input)) return chip;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {chip}
      {result.viewer_url && <LinkButton href={result.viewer_url} label="Open viewer"><OpenInNewIcon fontSize="small" /></LinkButton>}
      {result.short_url && <CopyButton text={result.short_url} />}
    </span>
  );
};

export const renderFile = input => {
  if (!isRow(input) || !input.file_url) return null;
  return (
    <LinkButton href={input.file_url} label="Download file" title={input.file_name ? `Download ${input.file_name}` : 'Download file'}>
      <GetAppIcon />
    </LinkButton>
  );
};
